import { type CSSProperties, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';

export type DrawerSide = 'left' | 'right' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /** CSS padding for the body slot. Default '24px'. Pass 0 / '0' to disable
   *  when the children manage their own internal padding (e.g. EmailComposer). */
  bodyPadding?: string | number;
}

const SIDE_SIZE: Record<DrawerSide, Record<DrawerSize, string>> = {
  right:  { sm: '400px', md: '560px', lg: '720px' },
  left:   { sm: '400px', md: '560px', lg: '720px' },
  bottom: { sm: '50vh',  md: '70vh',  lg: '90vh'  },
};

const TRANSFORM_OUT: Record<DrawerSide, string> = {
  right:  'translateX(100%)',
  left:   'translateX(-100%)',
  bottom: 'translateY(100%)',
};

const POSITION: Record<DrawerSide, CSSProperties> = {
  right:  { top: 0, right: 0, bottom: 0 },
  left:   { top: 0, left:  0, bottom: 0 },
  bottom: { left: 0, right: 0, bottom: 0 },
};

const SHADOW: Record<DrawerSide, string> = {
  right:  '-20px 0 60px rgba(0,0,0,0.3)',
  left:   '20px 0 60px rgba(0,0,0,0.3)',
  bottom: '0 -20px 60px rgba(0,0,0,0.3)',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

const DURATION = 200;

export function Drawer({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  children,
  footer,
  closeOnBackdrop = true,
  bodyPadding = '24px',
}: DrawerProps) {
  const drawerRef    = useRef<HTMLDivElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);
  const [mounted,  setMounted]  = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Drive mounted/animateIn from open prop
  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setAnimateIn(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setAnimateIn(false);
      const t = setTimeout(() => setMounted(false), DURATION + 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Scroll lock + auto-focus + restore focus
  useEffect(() => {
    if (!open) return;
    prevFocusRef.current = document.activeElement as HTMLElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = requestAnimationFrame(() => {
      drawerRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
      prevFocusRef.current?.focus();
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const els = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (els.length === 0) { e.preventDefault(); return; }
      const first = els[0]!;
      const last  = els[els.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, [open]);

  if (!mounted) return null;

  const dim        = SIDE_SIZE[side][size];
  const transition = `transform ${DURATION}ms ease-out, opacity ${DURATION}ms ease`;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={closeOnBackdrop ? onClose : undefined}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 800,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(1px)',
          opacity: animateIn ? 1 : 0,
          transition: `opacity ${DURATION}ms ease`,
        }}
      />

      {/* Panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          zIndex: 801,
          ...POSITION[side],
          width:    side !== 'bottom' ? dim     : undefined,
          height:   side === 'bottom' ? dim     : undefined,
          maxWidth: side !== 'bottom' ? '100vw' : undefined,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--evo-bg-2)',
          borderLeft:  side === 'right'  ? '1px solid var(--evo-stroke)' : undefined,
          borderRight: side === 'left'   ? '1px solid var(--evo-stroke)' : undefined,
          borderTop:   side === 'bottom' ? '1px solid var(--evo-stroke)' : undefined,
          boxShadow: SHADOW[side],
          transform: animateIn ? 'translate(0)' : TRANSFORM_OUT[side],
          transition,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        {title != null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid var(--evo-stroke)',
              flexShrink: 0,
            }}
          >
            <h2
              id="drawer-title"
              style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
                color: 'var(--evo-fg-1)',
              }}
            >
              {title}
            </h2>
            <IconButton
              icon={<Icon name="close" size={14} />}
              ariaLabel="close"
              onClick={onClose}
              variant="ghost"
              tone="muted"
              size="sm"
            />
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: bodyPadding }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 24px',
              borderTop: '1px solid var(--evo-stroke)',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
